import {
  Link,
  Navigate,
  Outlet,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Button, TuteroLogo, type IconName } from '@tutero/ui';

import { isStaff } from '@/lib/auth';
import { askPartner, bounceTo, lastAttempt, STRANDED_MS } from '@/lib/bridge';
import { watchCluster } from '@/lib/cluster';
import { env } from '@/lib/env';
import { identifyTelemetry, reportError, trackScreen } from '@/lib/telemetry';

import { AppSidebar } from './AppSidebar';
import { LoginForm } from './auth/LoginForm';
import {
  REQUEST_ACCESS_PATH,
  RequestAccessPage,
} from './auth/RequestAccessPage';
import { Loading } from './Loading';
import { PageIcon } from './PageTitle';
import { isSuperUser } from './permissions/staff';
import { isQuiet, useQuietShell } from './quiet';
import { StaffProvider, useSession, useStaff } from './session';
import { SectionTabs } from './SectionTabs';
import { grantable, pageAt, sectionsFor, tabsAt } from './tools';

/**
 * The shell (`admin-is-an-app` D1, D3): who is signed in, the nav their grants open, and
 * the tool on show. A visitor signs in here (`staff-sign-in-here` D1, which reverses the
 * redirect to the teacher's app; the one auth Worker is still the only auth); a signed-in
 * teacher is told this is staff's, whichever way their session was issued (D10).
 *
 * One address is answered before either of those: `/admin-access`, where someone asks for
 * access (`request-access-is-a-page` D1). A requester is exactly a signed-in non-staff
 * user, so the refusal that names a teacher would otherwise swallow the second face of
 * their own flow.
 */
export function Root() {
  const session = useSession();
  const requesting = useRequestingAccess();
  useEffect(() => {
    if (session.data) void identifyTelemetry(session.data.id);
  }, [session.data]);
  /* Every open tab follows the cookie (`manifests-draw-the-nav` D11). */
  useEffect(() => watchCluster({ quiet: isQuiet }), []);
  if (session.isPending) {
    return <Waiting />;
  }
  if (session.isError) {
    return (
      <Centered>
        <p role="alert" className="text-destructive text-regular-m">
          The session could not be read. Reload to try again.
        </p>
      </Centered>
    );
  }
  /* Before both refusals, and one `return` for both, so the typed password survives the
     session read that turns a visitor into a requester (`request-access-is-a-page` D1). */
  if (requesting && !isStaff(session.data?.role)) {
    return <RequestAccessPage />;
  }
  if (!session.data) {
    return <SignedOut requesting={requesting} />;
  }
  /*
   * A session this app holds no staff role for is a requester, whatever address they came
   * to: that is the whole of who the request page is for, and `RequestAccessPage` already
   * draws every state they can be in — the account made and the inbox unproved, the phone
   * owed, the request on file, the request that never filed.
   *
   * So they are sent there rather than shown a refusal with a Log out and nothing else.
   * The refusal was the only answer before there was a door; with one, it was a dead end,
   * and the worse half was mid-flow: somebody who signed up through Request Access and
   * typed `/` was bounced to it with no way back to the step they were on.
   *
   * A navigation and not a draw, unlike the two refusals above: the address should be the
   * page, so a reload and a bookmark land where they were. `replace`, because the address
   * they typed is not one to go back to.
   */
  if (!isStaff(session.data.role)) {
    return <Navigate to={REQUEST_ACCESS_PATH} replace />;
  }
  /* VITE_DEV_PERMS fills the nav with every tool locally; the backend still enforces (A4). */
  /* Beside, not instead of, the session's own: a Super User stays one (`isSuperUser`). */
  const staff = env.VITE_DEV_PERMS
    ? {
        ...session.data,
        perms: [...new Set([...grantable(), ...session.data.perms])],
      }
    : session.data;
  return (
    <StaffProvider staff={staff}>
      <Layout />
    </StaffProvider>
  );
}

/**
 * Whether the address is Request Access's. The location and not a param, because `Root`
 * draws this one before the route tree is reached at all: `Outlet` lives in `Layout`, which
 * only runs past the staff gate (`request-access-is-a-page` D2). A typed trailing slash is
 * the same address: this router carries no `trailingSlash` normalisation.
 */
function useRequestingAccess(): boolean {
  return useRouterState({
    select: (state) =>
      state.location.pathname.replace(/\/+$/, '') === REQUEST_ACCESS_PATH,
  });
}

/** When this page load's wait began. */
let waitingSince: number | null = null;

/**
 * The lockup over a bar filling from the left, faded in after a beat so a quick sign-in shows
 * nothing at all. Drawn while the session is read, and again while the bridge is being asked for
 * one, which is the same wait to the person waiting, so the second picks the fill up where the
 * first left it rather than starting over.
 *
 * The session read reports no progress, so the fill is a pace and not a measure: it slows towards
 * the end and stops short, and the bar states no value to a screen reader.
 */
function Waiting() {
  const [elapsed] = useState(() => {
    waitingSince ??= performance.now();
    return performance.now() - waitingSince;
  });
  return (
    <Centered>
      <div className="flex flex-col gap-20 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:delay-150 motion-safe:duration-500">
        <TuteroLogo type="lockup" height={72} alt="Tutero Admin" />
        <div
          role="progressbar"
          aria-label="Signing in"
          className="bg-border h-2 overflow-hidden rounded-full"
        >
          <div
            className="motion-safe:motion-sign-in-fill h-full w-full rounded-full bg-blue-500 motion-reduce:w-1/3"
            style={{ animationDelay: `-${elapsed}ms` }}
          />
        </div>
      </div>
    </Centered>
  );
}

/**
 * Signed out: the admin's own form, or — where this host carries its own session
 * (`the-session-crosses-to-tutero-com` D10–D12) — one ask next door first.
 *
 * The session cookie is host-only, so this host holds a session or none and cannot read
 * the partner's. Asking on arrival rather than minting at sign-in is 3.0's inversion and
 * its reason: the redirect lands on a page load that was happening anyway and replaces a
 * login form rather than a working page, and a staff member who only ever opens one host
 * is never redirected at all.
 *
 * The decision is taken once, when this surface first draws, and then frozen: it reads the
 * attempt the effect is about to record, so recomputing it on a later render would flip it
 * to "do not ask" and flash the form over a bounce already committing. `useState`'s
 * initialiser rather than an effect for the same reason — an effect runs after the paint,
 * and the flash is the whole thing being avoided. The write and the navigation stay the
 * effect's, so the render itself is only a read.
 */
function SignedOut({ requesting }: { requesting: boolean }) {
  const [asking] = useState(() =>
    bounceTo({
      bridged: env.VITE_SESSION_BRIDGE,
      pathname: window.location.pathname,
      search: window.location.search,
      origin: window.location.origin,
      requesting,
      attempted: lastAttempt(),
      now: Date.now(),
    }),
  );
  const [stranded, setStranded] = useState(false);
  useEffect(() => {
    if (asking === null) return;
    askPartner(asking);
    /* A host the pairing does not name answers 204, and a 204 to a navigation leaves the
       browser exactly here: draw the form rather than wait forever on a bounce that has
       already happened and done nothing. */
    const timer = setTimeout(() => setStranded(true), STRANDED_MS);
    return () => clearTimeout(timer);
  }, [asking]);
  if (asking !== null && !stranded) return <Waiting />;
  return (
    <Centered>
      <LoginForm />
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  useQuietShell();
  return (
    <main
      data-centered
      className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center"
    >
      {children}
    </main>
  );
}

function Layout() {
  return (
    <AppSidebar>
      <Outlet />
    </AppSidebar>
  );
}

/**
 * Home (`manifests-draw-the-nav` D1b): the lockup, centred, until a tool is chosen — the
 * Flutter admin's own empty page. The nav already lists every tool the session opens, so
 * a grid of cards restated it. A session holding no grants keeps the sentence: a bare
 * lockup tells a staff member nothing about why their nav is empty.
 */
export function Home() {
  const staff = useStaff();
  const sections = sectionsFor(staff.perms);
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
      {/* The artwork is the heading: its `alt` is the name a screen reader reads. */}
      <h1>
        <TuteroLogo type="lockup" height={64} alt="Tutero Admin" />
      </h1>
      {sections.length === 0 ? (
        <p className="text-regular-m">
          You have no tools yet. Ask a staff manager to grant you the ones you
          need.
        </p>
      ) : null}
    </div>
  );
}

/**
 * A tool's page, behind its grant (`admin-app` › "A staff member without the permission
 * opens a tool's URL"): without it, the refusal, and the tool's chunk is never fetched,
 * so none of its calls are made. An outside link has no page here (D4).
 */
export function ToolPage() {
  const { tool: id } = useParams({ strict: false });
  const staff = useStaff();
  const tool = pageAt(id);
  const open = tool !== undefined && staff.perms.includes(tool.permission);
  if (!tool) {
    return (
      <Refusal title="No such tool">There is no tool at this address.</Refusal>
    );
  }
  if (!open) {
    return (
      <Refusal title={`You cannot open ${tool.title}`}>
        It needs the grant <code>{tool.permission}</code>. Ask a staff manager
        for it.
      </Refusal>
    );
  }
  const surface = (
    <Surface
      id={tool.id}
      title={tool.title}
      icon={tool.icon}
      Page={tool.Page}
    />
  );
  const tabs = tabsAt(tool.id, staff.perms);
  return tabs ? (
    <SectionTabs page={tabs} active={tool.id}>
      {surface}
    </SectionTabs>
  ) : (
    surface
  );
}

const Permissions = lazy(() => import('./permissions/PermissionsScreen'));

/**
 * User Permissions (`permissions-is-in-the-profile` D1): a surface of the shell's own at
 * a route of the shell's, drawn like a tool's page — lazily, so the largest screen in the
 * admin is not in everybody's first bundle, and behind the same boundary, so a throw
 * costs the page and not the admin. Super Users only, as the Flutter admin's menu: anybody
 * else is refused before the chunk is fetched. The auth Worker still refuses the reads and
 * the writes itself, and the screen draws its refusals (D4).
 */
export function PermissionsPage() {
  const staff = useStaff();
  if (!isSuperUser(staff)) {
    return (
      <Refusal title="You cannot open User Permissions">
        It is for Super Users. Ask one to make you one.
      </Refusal>
    );
  }
  return (
    <Surface
      id="permissions"
      title="User Permissions"
      icon="lock"
      Page={Permissions}
    />
  );
}

/** A page at a route, with its boundary, its spinner and its screen report: a tool's, or one of the shell's own. */
function Surface({
  id,
  title,
  icon,
  Page,
}: {
  id: string;
  title: string;
  /** Its glyph in the nav, which its page's heading draws too. */
  icon: IconName;
  Page: React.ComponentType;
}) {
  /* Here and not in `ToolPage`: this draws only what a refusal did not, so the report follows the page. */
  useEffect(() => {
    trackScreen(id);
  }, [id]);
  return (
    <SurfaceBoundary id={id} title={title}>
      <Suspense fallback={<Loading label={`Loading ${title}`} />}>
        <PageIcon value={icon}>
          <Page />
        </PageIcon>
      </Suspense>
    </SurfaceBoundary>
  );
}

/** A page's own boundary (D12), outside the `Suspense` so a chunk that fails to load lands in it too; the address resets it. */
function SurfaceBoundary({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  /* The params, never the location: the two tear mid-navigation, and the address must change with the page it draws. */
  const path = useToolPath();
  return (
    <ErrorBoundary
      resetKeys={[id, path]}
      FallbackComponent={(props: FallbackProps) => (
        <SurfaceFallback title={title} {...props} />
      )}
      onError={(error, info) => caught(id, error, info)}
    >
      {children}
    </ErrorBoundary>
  );
}

function SurfaceFallback({
  title,
  resetErrorBoundary,
}: FallbackProps & { title: string }) {
  return (
    <Refusal
      title={`${title} hit an error`}
      action={
        <Button variant="outline" onClick={() => resetErrorBoundary()}>
          Retry
        </Button>
      }
    >
      Something in the page threw while it was drawing. Nothing else in the
      admin is affected.
    </Refusal>
  );
}

function Refusal({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  useQuietShell();
  return (
    <section aria-labelledby="refusal" className="flex flex-col gap-2">
      <h1 id="refusal" className="text-headline-4">
        {title}
      </h1>
      <p className="text-regular-m">{children}</p>
      {/* Its own row: a `Button` in the column would stretch to the page's width. */}
      {action ? <div className="flex">{action}</div> : null}
      <Link to="/" className="text-primary text-regular-m underline">
        Back to Admin
      </Link>
    </section>
  );
}

/** The shell's own boundary (D12), above the router in `src/main.tsx`: sign-in, the session and the nav have nothing below them to catch theirs. */
export function ShellBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={ShellFallback}
      onError={(error, info) => caught('shell', error, info)}
    >
      {children}
    </ErrorBoundary>
  );
}

function ShellFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <Centered>
      <h1 className="text-headline-4">The admin hit an error</h1>
      <p className="text-regular-m">
        Retry draws it again; a reload starts it over. Report it as an issue if
        it keeps happening.
      </p>
      <Button variant="outline" onClick={() => resetErrorBoundary()}>
        Retry
      </Button>
      {/* An anchor, not a `Link`: the router is what may have thrown. */}
      <a href="/" className="text-primary text-regular-m underline">
        Reload the admin
      </a>
    </Centered>
  );
}

/** A catch: one console line naming what fell — the only signal with no DSN — and Sentry's event. */
function caught(region: string, error: unknown, info: React.ErrorInfo): void {
  console.error(
    [`${region} hit an error: ${described(error)}`, info.componentStack ?? '']
      .filter(Boolean)
      .join('\n'),
  );
  void reportError(error, { region, componentStack: info.componentStack });
}

/** An error as words: its stack, which names it and its message, or its text. */
function described(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? `${error.name}: ${error.message}`;
  }
  return String(error);
}

/** The rest of the address under a tool's own, `''` at its root: the tool's to read. */
export function useToolPath(): string {
  const params = useParams({ strict: false }) as { _splat?: string };
  return params._splat ?? '';
}
